import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Save,
  Business,
  LocationOn,
  Contacts,
  Payment,
  Settings,
  Edit,
  Cancel,
  ContentCopy,
  Check,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { employerApi } from "../../services/apiClient";
import { toast } from "react-toastify";
import EditContactsModal from "../../components/EditContactsModal";
import AddressSection from "../../components/AddressSection";
import PaymentMethodSection from "../../components/PaymentMethodSection";

interface CompanyData {
  companyID: string;
  name: string;
  address: string;
  addressObject: {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    county: string;
    country: string;
    postcode: string;
  };
  postcode: string;
  hrContacts: Array<{ name: string; email: string; phone: string }>;
  financeContacts: Array<{ name: string; email: string; phone: string }>;
  payrollContacts: Array<{ name: string; email: string; phone: string }>;
  batchConfiguration: {
    sacrificeBatch: boolean;
    sacrificeDayOfMonth: number | null;
  };
  paymentDetails: {
    status: string | null;
    authorisationURL: string | null;
  } | null;
}

const Configuration = () => {
  const queryClient = useQueryClient();
  const [companyName, setCompanyName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [nameSuccessMessage, setNameSuccessMessage] = useState("");
  const [editContactsModalOpen, setEditContactsModalOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [nameConfirmDialogOpen, setNameConfirmDialogOpen] = useState(false);
  const [addressConfirmDialogOpen, setAddressConfirmDialogOpen] = useState(false);
  const [sacrificeDayOfMonth, setSacrificeDayOfMonth] = useState<number | null>(null);
  const [savingBatchConfig, setSavingBatchConfig] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch company data
  const {
    data: companyResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["companyConfiguration"],
    queryFn: async () => {
      const response = await employerApi.get<{ success: boolean; data: CompanyData }>("/company");
      return response;
    },
  });

  const company = companyResponse?.data;

  // Initialize company name and batch config when data loads
  React.useEffect(() => {
    if (company?.name) {
      setCompanyName(company.name);
    }
    if (company?.batchConfiguration?.sacrificeDayOfMonth !== undefined) {
      setSacrificeDayOfMonth(company.batchConfiguration.sacrificeDayOfMonth);
    }
  }, [company?.name, company?.batchConfiguration?.sacrificeDayOfMonth]);

  // Mutation for updating company
  const updateCompanyMutation = useMutation({
    mutationFn: async (updates: any) => {
      const response = await employerApi.put<{ success: boolean; data: CompanyData; message: string }>(
        "/company",
        updates
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companyConfiguration"] });
      toast.success("Company configuration updated successfully");
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || "Failed to update company configuration";
      toast.error(errorMessage);
    },
  });

  const handleEditName = () => {
    setNameConfirmDialogOpen(true);
  };

  const handleConfirmEditName = () => {
    setNameConfirmDialogOpen(false);
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setCompanyName(company?.name || "");
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    if (!companyName.trim()) {
      toast.error("Company name cannot be empty");
      return;
    }

    if (companyName === company?.name) {
      setIsEditingName(false);
      return; // No changes
    }

    setSavingName(true);
    setNameSuccessMessage("");

    try {
      await updateCompanyMutation.mutateAsync({ name: companyName });
      setNameSuccessMessage("Company name updated successfully");
      setIsEditingName(false);
      setTimeout(() => {
        setNameSuccessMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error saving company name:", error);
    } finally {
      setSavingName(false);
    }
  };

  const handleEditAddress = () => {
    setAddressConfirmDialogOpen(true);
  };

  const handleConfirmEditAddress = () => {
    setAddressConfirmDialogOpen(false);
    setIsEditingAddress(true);
  };

  const handleCancelEditAddress = () => {
    setIsEditingAddress(false);
  };

  const handleSaveBatchConfig = async () => {
    if (sacrificeDayOfMonth === company?.batchConfiguration?.sacrificeDayOfMonth) {
      return; // No changes
    }

    setSavingBatchConfig(true);
    try {
      await updateCompanyMutation.mutateAsync({
        batchConfiguration: {
          sacrificeDayOfMonth: sacrificeDayOfMonth,
        } as any,
      });
      toast.success("Salary sacrifice day updated successfully");
    } catch (error) {
      console.error("Error saving batch configuration:", error);
    } finally {
      setSavingBatchConfig(false);
    }
  };

  const handleSaveContacts = async (updatedContacts: {
    hrContacts: Array<{ name: string; email: string; phone: string }>;
    financeContacts: Array<{ name: string; email: string; phone: string }>;
    payrollContacts: Array<{ name: string; email: string; phone: string }>;
  }) => {
    try {
      await updateCompanyMutation.mutateAsync(updatedContacts);
      setEditContactsModalOpen(false);
    } catch (error) {
      console.error("Error saving contacts:", error);
      throw error; // Re-throw to let modal handle the error
    }
  };

  const handleSaveAddress = async (
    address: string,
    addressObject: CompanyData["addressObject"],
    postcode: string
  ) => {
    try {
      await updateCompanyMutation.mutateAsync({
        address,
        addressObject,
        postcode,
      });
    } catch (error) {
      console.error("Error saving address:", error);
      throw error; // Re-throw to let AddressSection handle the error
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load company configuration. Please try again later.
        </Alert>
      </Box>
    );
  }

  if (!company) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Company data not found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: "auto" }}>
      {/* Header Section - Profile Style */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 2,
          background: "#44444450",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 1.5,
              bgcolor: "rgba(255, 255, 255, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Business sx={{ fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>
              {company.name}
            </Typography>
            {/* <Typography variant="body2" sx={{ opacity: 0.9, fontSize: "0.875rem", mb: 1 }}>
              Company ID: {company.companyID}
            </Typography> */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: "0.875rem" }}>
                Copy unique company link for employee applications
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={copied ? <Check /> : <ContentCopy />}
                onClick={async () => {
                  const link = `https://cyclesaver.co.uk/subscriptions?code=${company.companyID}`;
                  try {
                    await navigator.clipboard.writeText(link);
                    setCopied(true);
                    toast.success("Link copied to clipboard");
                    setTimeout(() => setCopied(false), 2000);
                  } catch (error) {
                    console.error("Failed to copy:", error);
                    toast.error("Failed to copy link");
                  }
                }}
                sx={{
                  minWidth: "auto",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.75rem",
                  borderColor: "rgba(0, 0, 0, 0.5)",
                  color: "black",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.8)",
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Two Column Grid Layout */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
        {/* Left Column */}
        <Box sx={{ flex: { md: "0 0 58.333%" } }}>
          {/* Company Name Section */}
          <Card elevation={2} sx={{ mb: 2, borderRadius: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Business sx={{ color: "primary.main" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Company Name
                  </Typography>
                </Box>
                {!isEditingName && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    onClick={handleEditName}
                  >
                    Edit
                  </Button>
                )}
              </Box>

              {nameSuccessMessage && (
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  {nameSuccessMessage}
                </Alert>
              )}

              {isEditingName ? (
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      if (nameSuccessMessage) setNameSuccessMessage("");
                    }}
                    disabled={savingName}
                    sx={{ flex: 1 }}
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleCancelEditName}
                      disabled={savingName}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={savingName ? <CircularProgress size={16} /> : <Save />}
                      onClick={handleSaveName}
                      disabled={savingName || companyName === company.name || !companyName.trim()}
                    >
                      {savingName ? "Saving..." : "Save"}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body1" sx={{ py: 1 }}>
                  {company?.name || "Not set"}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Address Section */}
          <Card elevation={2} sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocationOn sx={{ color: "primary.main" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Company Address
                  </Typography>
                </Box>
                {!isEditingAddress && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    onClick={handleEditAddress}
                  >
                    Edit
                  </Button>
                )}
              </Box>
              {isEditingAddress ? (
                <Box>
                  <AddressSection
                    address={company.address}
                    addressObject={company.addressObject}
                    postcode={company.postcode}
                    onSave={async (address, addressObject, postcode) => {
                      await handleSaveAddress(address, addressObject, postcode);
                      setIsEditingAddress(false);
                    }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleCancelEditAddress}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  {company.addressObject?.addressLine1 ? (
                    <Box sx={{ py: 1 }}>
                      <Typography variant="body2">{company.addressObject.addressLine1}</Typography>
                      {company.addressObject.addressLine2 && (
                        <Typography variant="body2">{company.addressObject.addressLine2}</Typography>
                      )}
                      {company.addressObject.addressLine3 && (
                        <Typography variant="body2">{company.addressObject.addressLine3}</Typography>
                      )}
                      {(company.addressObject.county || company.addressObject.country) && (
                        <Typography variant="body2">
                          {[company.addressObject.county, company.addressObject.country].filter(Boolean).join(", ")}
                        </Typography>
                      )}
                      {company.postcode && (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {company.postcode}
                        </Typography>
                      )}
                    </Box>
                  ) : company.address ? (
                    <Typography variant="body2" sx={{ py: 1 }}>
                      {company.address}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No address set
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Contacts Section */}
          <Card elevation={2} sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Contacts sx={{ color: "primary.main" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Contacts
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => setEditContactsModalOpen(true)}
                >
                  Edit Contacts
                </Button>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                  HR Contacts
                </Typography>
                {company.hrContacts && company.hrContacts.length > 0 ? (
                  company.hrContacts.map((contact, index) => (
                    <Box key={index} sx={{ mb: 1.5, pl: 2, py: 1, bgcolor: "grey.50", borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {contact.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {contact.email}
                      </Typography>
                      {contact.phone && (
                        <Typography variant="body2" color="text.secondary">
                          {contact.phone}
                        </Typography>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                    No HR contacts configured
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                  Finance Contacts
                </Typography>
                {company.financeContacts && company.financeContacts.length > 0 ? (
                  company.financeContacts.map((contact, index) => (
                    <Box key={index} sx={{ mb: 1.5, pl: 2, py: 1, bgcolor: "grey.50", borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {contact.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {contact.email}
                      </Typography>
                      {contact.phone && (
                        <Typography variant="body2" color="text.secondary">
                          {contact.phone}
                        </Typography>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                    No Finance contacts configured
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                  Payroll Contacts
                </Typography>
                {company.payrollContacts && company.payrollContacts.length > 0 ? (
                  company.payrollContacts.map((contact, index) => (
                    <Box key={index} sx={{ mb: 1.5, pl: 2, py: 1, bgcolor: "grey.50", borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {contact.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {contact.email}
                      </Typography>
                      {contact.phone && (
                        <Typography variant="body2" color="text.secondary">
                          {contact.phone}
                        </Typography>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                    No Payroll contacts configured
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Right Column */}
        <Box sx={{ flex: { md: "0 0 41.667%" } }}>
          {/* Payment Method Section */}
          <Card elevation={2} sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <PaymentMethodSection paymentDetails={company.paymentDetails} />
            </CardContent>
          </Card>

          {/* Batch Configuration Section - Only show if sacrificeBatch is true */}
          {company.batchConfiguration?.sacrificeBatch && (
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Settings sx={{ color: "primary.main" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Payroll Salary Sacrifice Report Configuration
                  </Typography>
                </Box>
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Reports will be sent on the day of the month specified below.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1 }}>
                    <TextField
                      type="number"
                      value={sacrificeDayOfMonth || ""}
                      onChange={(e) => {
                        const value = e.target.value === "" ? null : parseInt(e.target.value, 10);
                        if (value === null || (value >= 1 && value <= 12)) {
                          setSacrificeDayOfMonth(value);
                        }
                      }}
                      inputProps={{ min: 1, max: 12 }}
                      sx={{ width: 100 }}
                      disabled={savingBatchConfig}
                      error={sacrificeDayOfMonth !== null && (sacrificeDayOfMonth < 1 || sacrificeDayOfMonth > 12)}
                      helperText={
                        sacrificeDayOfMonth !== null && (sacrificeDayOfMonth < 1 || sacrificeDayOfMonth > 12)
                          ? "Must be between 1 and 12"
                          : ""
                      }
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={savingBatchConfig ? <CircularProgress size={16} /> : <Save />}
                      onClick={handleSaveBatchConfig}
                      disabled={
                        savingBatchConfig ||
                        sacrificeDayOfMonth === company.batchConfiguration?.sacrificeDayOfMonth ||
                        (sacrificeDayOfMonth !== null && (sacrificeDayOfMonth < 1 || sacrificeDayOfMonth > 12))
                      }
                    >
                      {savingBatchConfig ? "Saving..." : "Save"}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Select the day of the month (1-12) when the salary sacrifice report will be sent.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {/* Edit Contacts Modal */}
      <EditContactsModal
        open={editContactsModalOpen}
        onClose={() => setEditContactsModalOpen(false)}
        company={company}
        onSave={handleSaveContacts}
      />

      {/* Name Edit Confirmation Dialog */}
      <Dialog open={nameConfirmDialogOpen} onClose={() => setNameConfirmDialogOpen(false)}>
        <DialogTitle>Edit Company Name</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Updating the company name will change the details on invoices sent to your company by CycleSaver. Do you wish to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmEditName} variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Edit Confirmation Dialog */}
      <Dialog open={addressConfirmDialogOpen} onClose={() => setAddressConfirmDialogOpen(false)}>
        <DialogTitle>Edit Company Address</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Updating the address will change the details on invoices sent to your company by CycleSaver. Do you wish to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmEditAddress} variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Configuration;
